import { useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTrigger,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  toaster,
} from '@optiaxiom/react';
import { IconArrowsRotate, IconEllipsisSolid, IconTrashCan } from '@optimizely/axiom-icons';
import { useDecision } from '@optimizely/react-sdk';

import { EmptyPage } from '@frontend/components';
import { RUNTIME_ENV } from '@frontend/envConfig';
import { useInstanceId } from '@frontend/hooks';
import { ToolService } from '@frontend/types';

import {
  useDeleteToolRegistry,
  useGetToolRegistries,
  useSyncToolRegistry,
} from '../../hooks/useToolsRegistry';

// Simple date format utility function
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  } catch {
    return dateString;
  }
};

type ToolRegistryActionsProps = {
  registryId: string;
  registryName: string;
  isDeleting: boolean;
  onDelete: (registryId: string) => void;
  onSync: (registryId: string) => void;
  isSyncing: boolean;
  isDeleteActionAvailable: boolean;
};

const ToolRegistryActions = ({
  registryId,
  registryName,
  isDeleting,
  onDelete,
  onSync,
  isSyncing,
  isDeleteActionAvailable,
}: ToolRegistryActionsProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger appearance="subtle" aria-label="Actions" icon={<IconEllipsisSolid />} />
      <DropdownMenuContent side="left">
        <DropdownMenuItem
          onSelect={e => e.preventDefault()}
          className="text-red-600"
          disabled={isDeleting || isSyncing}
          onClick={() => onSync(registryId)}
        >
          {isSyncing ? <Spinner size="xs" /> : <IconArrowsRotate size={16} className="mr-2" />}
          Sync
        </DropdownMenuItem>
        {isDeleteActionAvailable && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <DropdownMenuItem
                onSelect={e => e.preventDefault()}
                className="text-red-600"
                disabled={isDeleting}
              >
                <IconTrashCan size={16} className="mr-2" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </DropdownMenuItem>
            </AlertDialogTrigger>

            <AlertDialogContent>
              <AlertDialogHeader>Delete Tool Registry?</AlertDialogHeader>
              <AlertDialogBody>
                Are you sure you want to delete the tool registry &quot;{registryName}&quot;? This
                action cannot be undone.
              </AlertDialogBody>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(registryId)} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Yes, delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const ToolsDisplay = ({ tools }: { tools: string[] }) => {
  if (!tools || tools.length === 0) {
    return <Text>None</Text>;
  }
  const displayTools = tools.slice(0, 3);
  const remainingCount = tools.length - 3;

  return (
    <Flex flexDirection="row" gap="2" flexWrap="wrap">
      {displayTools.map(tool => (
        <Badge intent="neutral" key={tool}>
          {tool}
        </Badge>
      ))}
      {remainingCount > 0 && <Badge intent="neutral">+{remainingCount}</Badge>}
    </Flex>
  );
};

type ToolsRegistryTabProps = {
  filterText?: string;
};

export function ToolsRegistryTab({ filterText = '' }: ToolsRegistryTabProps) {
  const { instanceId } = useInstanceId();
  const { toolRegistries, isPending, error } = useGetToolRegistries(instanceId || '');
  const { mutate: deleteMutation, isPending: isDeleting } = useDeleteToolRegistry(instanceId || '');
  const { mutate: syncMutation, isPending: isSyncing } = useSyncToolRegistry();
  const [decision] = useDecision('manage_system_tools');
  const canManageSystemTools = decision.enabled as boolean;

  useEffect(() => {
    if (error) {
      toaster.create(error.message || 'Failed to load tool registries', { type: 'danger' });
    }
  }, [error]);

  const handleDelete = (serviceId: string) => {
    deleteMutation(serviceId);
  };

  const handleSync = (registryId: string) => {
    syncMutation(registryId);
  };

  if (isPending) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spinner />
      </div>
    );
  }

  if (!toolRegistries || toolRegistries.length === 0) {
    return (
      <EmptyPage title="No tool registries found." description="Try adding a new tool registry." />
    );
  }

  // Filter registries based on the filterText input
  const filteredRegistries = filterText.trim()
    ? toolRegistries.filter(registry => {
        const lowerCaseFilter = filterText.toLowerCase();
        return (
          registry.name.toLowerCase().includes(lowerCaseFilter) ||
          registry.owner_email.toLowerCase().includes(lowerCaseFilter) ||
          registry.discovery_url.toLowerCase().includes(lowerCaseFilter)
        );
      })
    : toolRegistries;

  const isDeleteActionAvailable = (registry: ToolService) => {
    const isSystemTools = !registry.organization_id;
    if (isSystemTools) {
      // Allow delete in localdev
      if (RUNTIME_ENV === 'localdev') {
        return true;
      }
      return canManageSystemTools;
    }
    return true;
  };

  return (
    <Flex>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Discovery URL</TableHeaderCell>
            <TableHeaderCell>Owner</TableHeaderCell>
            <TableHeaderCell>Last Sync</TableHeaderCell>
            <TableHeaderCell>Tools</TableHeaderCell>
            <TableHeaderCell>Actions</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredRegistries.map(registry => (
            <TableRow key={registry.id}>
              <TableCell maxW="md">{registry.name}</TableCell>
              <TableCell maxW="md">{registry.discovery_url}</TableCell>
              <TableCell>{registry.owner_email}</TableCell>
              <TableCell>{formatDate(registry.date_last_synced)}</TableCell>
              <TableCell>
                {registry.tools && registry.tools.length > 0 ? (
                  <ToolsDisplay tools={registry.tools} />
                ) : (
                  <Text>None</Text>
                )}
              </TableCell>
              <TableCell>
                <ToolRegistryActions
                  registryId={registry.id}
                  registryName={registry.name}
                  isDeleting={isDeleting}
                  onDelete={handleDelete}
                  onSync={handleSync}
                  isSyncing={isSyncing}
                  isDeleteActionAvailable={isDeleteActionAvailable(registry)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Flex>
  );
}
